import React from 'react';
import { ArrowRight, Users, Calendar, Mail, Shield, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';

const CALENDLY_URL = 'https://calendly.com/airlius-hts/munger-ai-demo';

interface OptimizedHeroSectionProps {
  onStartClick?: () => void;
}

const OptimizedHeroSection: React.FC<OptimizedHeroSectionProps> = ({ onStartClick }) => {



  // Sticky navigation
  const StickyNav = () => (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src="/favicon.svg" 
            alt="Munger.ai" 
            className="h-8 w-8 mr-2"
          />
          <span className="font-bold text-lg sm:text-xl text-gray-900">Munger.ai</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button 
            size="sm" 
            className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4" 
            onClick={() => window.open(CALENDLY_URL, '_blank')}
          >
            <Phone className="w-3 h-3 mr-1" />
            Book a Call
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50">
      {/* Sticky Navigation */}
      <StickyNav />

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <div className="text-center">
          {/* Trust signals */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8"
          >
            <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs sm:text-sm">
              <Users className="w-3 h-3 mr-1" />
              70M+ Emails Sent
            </Badge>
            <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs sm:text-sm">
              <Calendar className="w-3 h-3 mr-1" />
              7,200 Meetings Booked
            </Badge>
            <Badge className="bg-green-50 text-green-700 border-green-200 text-xs sm:text-sm">
              <Mail className="w-3 h-3 mr-1" />
              40K Leads Generated
            </Badge>
          </motion.div>

          {/* Beta countdown */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-block mb-8"
          >
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-medium shadow-lg animate-pulse">
              🚀 Beta closes in 9 days – Only 37 seats left
            </div>
          </motion.div>

          {/* Main content wrapper */}
          <div className="relative">
            {/* Clear value proposition */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight px-4"
            >
              Your One-Input Pipeline to 50+ Meetings
            </motion.h1>

            {/* Supporting copy - concrete benefits */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto px-4"
            >
              Tell us what you sell. Our AI automatically finds ideal prospects, 
              writes personalized emails, and books qualified meetings on your calendar.
            </motion.p>

            {/* Accountability badge */}


            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => window.open(CALENDLY_URL, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Book a Call
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>


          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizedHeroSection; 